import React, { useEffect, useMemo, useState } from 'react';
import API_BASE_URL from '../config';
import { useAuth } from '../contexts/AuthContext';
import { useContent } from '../hooks/useContent';
import DeploymentManagement from './DeploymentManagement';
import { _productCache, _updatedLog } from '../services/iapService';
import '../styles/components/profileTabs.css';
import '../styles/components/admin.css';

interface AIModel {
  id: string;
  name: string;
  speed: string;
  cost: string;
  accuracy: string;
  current: boolean;
}

const AdminTab: React.FC = () => {
  const { token } = useAuth();
  const { profile } = useContent();
  const [signupOpen, setSignupOpen] = useState<boolean | null>(null);

  // Invite form state
  const [invEmail, setInvEmail] = useState('');
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // AI Model settings
  const [aiModels, setAiModels] = useState<AIModel[]>([]);
  const [currentAiModel, setCurrentAiModel] = useState<string>('');
  const [aiModelLoading, setAiModelLoading] = useState(false);

  // Trash retention settings
  const [trashRetentionDays, setTrashRetentionDays] = useState<number>(30);
  const [trashRetentionLoading, setTrashRetentionLoading] = useState(false);

  // Guest cleanup settings
  const [guestArchiveDays, setGuestArchiveDays] = useState<string>('90');
  const [guestDeleteDays, setGuestDeleteDays] = useState<string>('30');
  const [guestSettingsLoading, setGuestSettingsLoading] = useState(false);
  const [deletingArchivedGuests, setDeletingArchivedGuests] = useState(false);

  // Queue worker management
  const [workerStatus, setWorkerStatus] = useState<any>(null);
  const [restartingWorker, setRestartingWorker] = useState(false);

  const authHeader = useMemo(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  }), [token]);


  const fetchAuthConfig = async () => {
    try {
      const resp = await fetch(`${API_BASE_URL}/api/auth/config`);
      if (!resp.ok) return;
      const data = await resp.json();
      setSignupOpen(!!data.signup_open);
    } catch {}
  };

  const fetchAIModels = async () => {
    if (!token) return;
    try {
      const resp = await fetch(`${API_BASE_URL}/api/admin/ai-models`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!resp.ok) return;
      const data = await resp.json();
      setAiModels(data.models || []);
      setCurrentAiModel(data.current_model || '');
    } catch (e) {
      console.error('Failed to fetch AI models:', e);
    }
  };

  const updateAIModel = async (modelId: string) => {
    if (!token || aiModelLoading) return;
    setAiModelLoading(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/admin/ai-models`, {
        method: 'PUT',
        headers: authHeader,
        body: JSON.stringify({ model: modelId }),
      });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(text || 'Failed to update AI model');
      }
      const data = await resp.json();
      setCurrentAiModel(modelId);
      alert(`AI model updated to ${data.name}. Queue worker will use new model on next restart.`);
      await fetchAIModels();
    } catch (e: any) {
      alert(e.message || 'Failed to update AI model');
    } finally {
      setAiModelLoading(false);
    }
  };

  const fetchTrashRetention = async () => {
    if (!token) return;
    try {
      const resp = await fetch(`${API_BASE_URL}/api/settings/trash_retention_days`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!resp.ok) return;
      const data = await resp.json();
      setTrashRetentionDays(parseInt(data.value) || 30);
    } catch (e) {
      console.error('Failed to fetch trash retention:', e);
    }
  };

  const updateTrashRetention = async (days: number) => {
    if (!token || trashRetentionLoading) return;
    setTrashRetentionLoading(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/settings/trash_retention_days`, {
        method: 'PUT',
        headers: authHeader,
        body: JSON.stringify({ value: days.toString() }),
      });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(text || 'Failed to update trash retention');
      }
      alert(`Trash retention updated to ${days} days`);
    } catch (e: any) {
      alert(e.message || 'Failed to update trash retention');
    } finally {
      setTrashRetentionLoading(false);
    }
  };

  const fetchGuestSettings = async () => {
    if (!token) return;
    try {
      const [archiveResp, deleteResp] = await Promise.all([
        fetch(`${API_BASE_URL}/api/settings/guest_archive_days`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch(`${API_BASE_URL}/api/settings/guest_delete_days`, {
          headers: { 'Authorization': `Bearer ${token}` },
        })
      ]);
      if (archiveResp.ok) {
        const data = await archiveResp.json();
        setGuestArchiveDays(data.value || '90');
      }
      if (deleteResp.ok) {
        const data = await deleteResp.json();
        setGuestDeleteDays(data.value || '30');
      }
    } catch (e) {
      console.error('Failed to fetch guest settings:', e);
    }
  };

  const updateGuestArchiveDays = async (days: string) => {
    if (!token || guestSettingsLoading) return;
    setGuestSettingsLoading(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/settings/guest_archive_days`, {
        method: 'PUT',
        headers: authHeader,
        body: JSON.stringify({ value: days }),
      });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(text || 'Failed to update guest archive days');
      }
      alert(`Guest archive days updated to ${days} days`);
    } catch (e: any) {
      alert(e.message || 'Failed to update guest archive days');
    } finally {
      setGuestSettingsLoading(false);
    }
  };

  const updateGuestDeleteDays = async (days: string) => {
    if (!token || guestSettingsLoading) return;
    setGuestSettingsLoading(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/settings/guest_delete_days`, {
        method: 'PUT',
        headers: authHeader,
        body: JSON.stringify({ value: days }),
      });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(text || 'Failed to update guest delete days');
      }
      alert(`Guest delete days updated to ${days} days`);
    } catch (e: any) {
      alert(e.message || 'Failed to update guest delete days');
    } finally {
      setGuestSettingsLoading(false);
    }
  };

  const deleteArchivedGuestsNow = async () => {
    if (!token || deletingArchivedGuests) return;
    
    const confirmed = window.confirm(
      'Delete all archived guests immediately?\n\n' +
      'This will permanently delete all archived guest users and their items. ' +
      'This action cannot be undone.\n\n' +
      'Are you sure you want to continue?'
    );
    
    if (!confirmed) return;
    
    setDeletingArchivedGuests(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/auth/admin/guests/delete-archived`, {
        method: 'POST',
        headers: authHeader,
      });
      
      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || 'Failed to delete archived guests');
      }
      
      const data = await resp.json();
      alert(
        `Successfully deleted ${data.deleted_count || 0} archived guest(s) ` +
        `and ${data.items_deleted || 0} item(s).`
      );
    } catch (e: any) {
      alert(`Error: ${e.message || 'Failed to delete archived guests'}`);
    } finally {
      setDeletingArchivedGuests(false);
    }
  };

  const fetchQueueWorkerStatus = async () => {
    if (!token) return;
    try {
      const resp = await fetch(`${API_BASE_URL}/api/admin/queue-worker/status`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!resp.ok) return;
      const data = await resp.json();
      setWorkerStatus(data);
    } catch (e) {
      console.error('Failed to fetch queue worker status:', e);
    }
  };

  const restartQueueWorker = async () => {
    if (!token || restartingWorker) return;
    setRestartingWorker(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/admin/queue-worker/restart`, {
        method: 'POST',
        headers: authHeader,
      });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(text || 'Failed to restart queue worker');
      }
      alert('Queue worker restarted successfully');
      setTimeout(fetchQueueWorkerStatus, 2000);
    } catch (e: any) {
      alert(e.message || 'Failed to restart queue worker');
    } finally {
      setRestartingWorker(false);
    }
  };

  const updateSignupOpen = async (open: boolean) => {
    if (!token) return;
    try {
      const resp = await fetch(`${API_BASE_URL}/api/auth/admin/config`, {
        method: 'PUT',
        headers: authHeader,
        body: JSON.stringify({ signup_open: open }),
      });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(text || 'Failed to update signup config');
      }
      setSignupOpen(open);
    } catch (e: any) {
      alert(e.message || 'Failed to update signup config');
    }
  };

  const createInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || inviteLoading || !invEmail) return;
    setInviteLoading(true);
    setInviteError(null);
    setInviteToken(null);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/auth/admin/invitations`, {
        method: 'POST',
        headers: authHeader,
        body: JSON.stringify({ email: invEmail }),
      });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(text || 'Failed to create invite');
      }
      const data = await resp.json();
      setInviteToken(data.token);
      setInvEmail('');
    } catch (e: any) {
      setInviteError(e.message || 'Failed to create invite');
    } finally {
      setInviteLoading(false);
    }
  };

  const copyInvite = () => {
    if (!inviteToken) return;
    navigator.clipboard.writeText(inviteToken);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => {
    fetchAuthConfig();
    fetchAIModels();
    fetchTrashRetention();
    fetchGuestSettings();
    fetchQueueWorkerStatus();
  }, [token]);

  const [appVersion, setAppVersion] = React.useState<string>('');
  const [buildNumber, setBuildNumber] = React.useState<string>('');

  // Fetch app version
  React.useEffect(() => {
    fetch('/version.json')
      .then(res => res.json())
      .then(data => {
        setAppVersion(data.version || '');
        setBuildNumber(data.build || '');
      })
      .catch(() => {
        setAppVersion('');
        setBuildNumber('');
      });
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {/* Settings Section */}
      <div style={{ 
        background: 'var(--color-bg-primary)',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        overflow: 'hidden',
        margin: 0
      }}>
        {/* Version Header */}
        {appVersion && (
          <div style={{
            background: 'var(--color-bg-secondary)',
            padding: '12px 16px',
            textAlign: 'center',
            fontSize: '14px',
            color: 'var(--color-accent)',
            fontWeight: '500',
            borderBottom: '1px solid var(--color-border)',
            borderRadius: '12px 12px 0 0',
          }}>
            Version: {appVersion} {buildNumber && `(Build ${buildNumber})`}
          </div>
        )}
        
        <div className="profile-section">
          <h3>Settings</h3>
          
          {/* Signup Setting */}
          {signupOpen === null ? (
            <p>{profile.settings_loading}</p>
          ) : (
            <div className="admin-setting-row">
              <label className="admin-toggle-label">
                <span>{signupOpen ? profile.signup_open : profile.invitation_required}</span>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={!!signupOpen}
                    onChange={(e) => updateSignupOpen(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </label>
            </div>
          )}

          {/* Trash Retention Days */}
          <div className="admin-setting-row" style={{ marginTop: '16px' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', alignItems: 'flex-start' }}>
              <span style={{ fontWeight: '500', textAlign: 'left' }}>Trash Retention (Days)</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={trashRetentionDays}
                  onChange={(e) => setTrashRetentionDays(parseInt(e.target.value) || 30)}
                  disabled={trashRetentionLoading}
                  style={{ 
                    padding: '8px 12px', 
                    borderRadius: '4px', 
                    border: '1px solid #ddd',
                    fontSize: '14px',
                    width: '100px'
                  }}
                />
                <button
                  onClick={() => updateTrashRetention(trashRetentionDays)}
                  disabled={trashRetentionLoading}
                  className="admin-button"
                  style={{ padding: '8px 16px' }}
                >
                  {trashRetentionLoading ? 'Saving...' : 'Save'}
                </button>
              </div>
              <span style={{ fontSize: '12px', color: 'var(--color-text-muted)', textAlign: 'left' }}>
                Number of days to keep deleted items in trash before permanent deletion.
              </span>
            </label>
          </div>

          {/* Guest Archive Days */}
          <div className="admin-setting-row" style={{ marginTop: '16px' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', alignItems: 'flex-start' }}>
              <span style={{ fontWeight: '500', textAlign: 'left' }}>Archive Inactive Guests After (Days)</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={guestArchiveDays}
                  onChange={(e) => setGuestArchiveDays(e.target.value)}
                  disabled={guestSettingsLoading}
                  style={{ 
                    padding: '8px 12px', 
                    borderRadius: '4px', 
                    border: '1px solid #ddd',
                    fontSize: '14px',
                    width: '100px'
                  }}
                />
                <button
                  onClick={() => updateGuestArchiveDays(guestArchiveDays)}
                  disabled={guestSettingsLoading}
                  className="admin-button"
                  style={{ padding: '8px 16px' }}
                >
                  {guestSettingsLoading ? 'Saving...' : 'Save'}
                </button>
              </div>
              <span style={{ fontSize: '12px', color: 'var(--color-text-muted)', textAlign: 'left' }}>
                Guest users inactive for this many days will be archived (soft deleted).
              </span>
            </label>
          </div>

          {/* Guest Delete Days */}
          <div className="admin-setting-row" style={{ marginTop: '16px' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', alignItems: 'flex-start' }}>
              <span style={{ fontWeight: '500', textAlign: 'left' }}>Delete Archived Guests After (Days)</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={guestDeleteDays}
                  onChange={(e) => setGuestDeleteDays(e.target.value)}
                  disabled={guestSettingsLoading}
                  style={{ 
                    padding: '8px 12px', 
                    borderRadius: '4px', 
                    border: '1px solid #ddd',
                    fontSize: '14px',
                    width: '100px'
                  }}
                />
                <button
                  onClick={() => updateGuestDeleteDays(guestDeleteDays)}
                  disabled={guestSettingsLoading}
                  className="admin-button"
                  style={{ padding: '8px 16px' }}
                >
                  {guestSettingsLoading ? 'Saving...' : 'Save'}
                </button>
              </div>
              <span style={{ fontSize: '12px', color: 'var(--color-text-muted)', textAlign: 'left' }}>
                Archived guests will be permanently deleted after this many days.
              </span>
            </label>
            
            {/* Delete Archived Guests Now Button */}
            <button
              onClick={deleteArchivedGuestsNow}
              disabled={deletingArchivedGuests}
              style={{
                marginTop: '12px',
                padding: '10px 16px',
                backgroundColor: 'var(--color-danger)',
                color: 'var(--color-text-inverse)',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: deletingArchivedGuests ? 'not-allowed' : 'pointer',
                opacity: deletingArchivedGuests ? 0.6 : 1,
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                if (!deletingArchivedGuests) {
                  e.currentTarget.style.backgroundColor = '#b91c1c';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-danger)';
              }}
            >
              {deletingArchivedGuests ? 'Deleting...' : 'Delete All Archived Guests Now'}
            </button>
          </div>

          {/* AI Model Selection */}
          <div className="admin-setting-row" style={{ marginTop: '16px' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', alignItems: 'flex-start' }}>
              <span style={{ fontWeight: '500', textAlign: 'left' }}>AI Vision Model</span>
              <select
                value={currentAiModel}
                onChange={(e) => updateAIModel(e.target.value)}
                disabled={aiModelLoading}
                style={{ 
                  padding: '8px 12px', 
                  borderRadius: '4px', 
                  border: '1px solid #ddd',
                  fontSize: '14px',
                  cursor: aiModelLoading ? 'wait' : 'pointer',
                  width: '100%'
                }}
              >
                {aiModels.map(model => (
                  <option key={model.id} value={model.id}>
                    {model.name} - Speed: {model.speed}, Cost: {model.cost}, Accuracy: {model.accuracy}
                  </option>
                ))}
              </select>
              
              {/* Queue Worker Controls */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '4px', flexWrap: 'wrap' }}>
                <button
                  onClick={restartQueueWorker}
                  disabled={restartingWorker}
                  className="admin-button"
                  style={{ padding: '6px 12px', fontSize: '13px' }}
                >
                  {restartingWorker ? 'Restarting...' : '🔄 Restart Queue Worker'}
                </button>
                {workerStatus && (
                  <span style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
                    Status: <strong style={{ color: workerStatus.status === 'online' ? 'var(--color-success)' : 'var(--color-danger)' }}>
                      {workerStatus.status}
                    </strong> | Restarts: {workerStatus.restarts}
                  </span>
                )}
              </div>
              
              <span style={{ fontSize: '12px', color: 'var(--color-text-muted)', textAlign: 'left' }}>
                Controls which AI model is used for image analysis. Click restart to apply changes immediately.
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* Invite User Section */}
      <div style={{ 
        background: 'var(--color-bg-primary)',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        overflow: 'hidden',
        margin: 0
      }}>
        <div className="profile-section">
          <h3>Invite User</h3>
          <form onSubmit={createInvite} className="admin-form invite-form-inline">
            <button 
              type="submit" 
              disabled={inviteLoading || !invEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(invEmail)} 
              className="admin-button invite-button"
            >
              {inviteLoading ? profile.invite_button_creating : profile.invite_button_create}
            </button>
            <input
              type="email"
              placeholder={profile.invite_email_placeholder}
              value={invEmail}
              onChange={(e) => setInvEmail(e.target.value)}
              required
              className="admin-input invite-email-input"
            />
          </form>
          {inviteError && <p style={{ color: 'red', marginTop: 8 }}>{inviteError}</p>}
          {inviteToken && (
            <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <code className="admin-token">{inviteToken}</code>
              <button type="button" onClick={copyInvite} className="admin-button">
                {copied ? profile.invite_button_copied : profile.invite_button_copy}
              </button>
              <div className="admin-pager-info">{profile.invite_share_message}</div>
            </div>
          )}
        </div>
      </div>

      {/* iOS IAP Tools */}
      <div style={{ 
        background: 'var(--color-bg-primary)',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        overflow: 'hidden',
        margin: 0
      }}>
        <div className="profile-section">
          <h3>iOS IAP Tools</h3>
          <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginBottom: '12px' }}>
            Clears local purchase ownership tracking. Use before switching test accounts. Does not cancel subscriptions.
          </p>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              onClick={() => {
                localStorage.removeItem('iap_purchase_owner');
                localStorage.removeItem('iap_processed_transactions');
                alert('IAP state cleared. Restart the app before testing a purchase.');
              }}
              className="admin-button"
              style={{ padding: '10px 20px' }}
            >
              Reset IAP State
            </button>
            <button
              onClick={() => {
                const cdv = (window as any).CdvPurchase;
                if (!cdv) { alert('CdvPurchase: NOT FOUND'); return; }
                const { store } = cdv;
                const storeProducts = (store.products || []).map((p: any) => `${p.id}(offers:${(p.offers||[]).length})`);
                const internalProducts = store._products?._products || store._products?.all || store._products || [];
                const internalLen = Array.isArray(internalProducts) ? internalProducts.length : typeof internalProducts;
                const regProducts = store.registeredProducts || store._registeredProducts || store.context?.registeredProducts || null;
                const productType = cdv.ProductType?.PAID_SUBSCRIPTION;
                const info = [
                  `Platform: ${cdv.Platform?.APPLE_APPSTORE}`,
                  `ProductType.PAID_SUB: ${productType}`,
                  `store.products: ${storeProducts.length}`,
                  `store._products internal: ${internalLen}`,
                  `store.registeredProducts: ${JSON.stringify(regProducts)?.substring(0, 80) ?? 'none'}`,
                  `store.get basic: ${!!store.get('com.wanderingsock.annual.basic')}`,
                  `store keys: ${Object.keys(store).join(',')}`,
                  `storeErrors: ${_updatedLog.filter(e => e.includes('STORE_ERR')).join(' | ').substring(0, 120) || 'none'}`,
                  `.updated log(${_updatedLog.length}): ${_updatedLog[0]?.substring(0, 60) || 'none'}`,
                ].join('\n');
                alert(info);
              }}
              className="admin-button"
              style={{ padding: '10px 20px' }}
            >
              Debug IAP State
            </button>
          </div>
        </div>
      </div>

      {/* Deployment Management Section */}
      <div style={{ 
        background: 'var(--color-bg-primary)',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        overflow: 'hidden',
        margin: 0
      }}>
        <div className="profile-section">
          <h3>Deployment & Services</h3>
          <DeploymentManagement token={token} />
        </div>
      </div>
    </div>
  );
};

export default AdminTab;
